import { PlusCircle, Truck } from "@phosphor-icons/react";
import { AddOnCard } from "components/add-on/AddOnCard.tsx";
import { DeliveryCard } from "components/address/AddressETD.tsx";
import AddressForm from "components/address/dialogs/AddressForm";
import AddressListDialog from "components/address/dialogs/AddressListDialog";
import AddressModify from "components/address/dialogs/AddressModify";
import AddressSection from "components/address/dialogs/AddressSection";
import Coupons from "components/checkout/Coupons";
import OOSDialog from "components/checkout/dialogs/OOSDialog";
import ShippingHandlesSection from "components/checkout/shipping-handles/ShippingHandlesSection.tsx";
import Rewards from "components/checkout/Rewards.tsx";
import { GenericDialog } from "components/common/dialogs/GenericDialog";
import OverlaySpinner from "components/common/loaders/OverlaySpinner";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import { useUserContext } from "lib/contexts/UserProvider";
import { getRequest } from "lib/core/apiClient.ts";
import { useAddressETD } from "lib/hooks/useAddressETD.ts";
import useSendAnalyticsEvent from "lib/hooks/useAnalytics";
import { useCheckoutErrors } from "lib/hooks/useCheckoutErrors";
import { useItemEdit } from "lib/hooks/useItemEdit.ts";
import { useLocale } from "lib/hooks/useLocale";
import { AddressCardFields } from "lib/types/address";
import { hasAutoAppliedCoupon } from "lib/utils/checkout";
import { analyticsEvents } from "lib/utils/constants";
import { checkMandatoryFields, classNames } from "lib/utils/helpers";
import { getETDText } from "lib/utils/merchant.ts";
import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

import BillingAddress from "components/address/BillingAddress";
import { AnimateExit } from "components/animation/AnimateExit.tsx";
import { AnimateLoading } from "components/animation/AnimateLoading.tsx";
import { PaymentSkeleton } from "components/common/skeleton/PaymentSkeleton.tsx";
import UpsellSkeleton from "components/common/skeleton/UpsellSkeleton.tsx";
import Payments from "components/payments/Payments";
import { useClarity } from "lib/hooks/useClarity.ts";
import { validateAddressFields, validateSelectedAddress } from "lib/utils/address-validation.ts";
import UpSellAndWishlistSection from "../../upsell/UpSellSection.tsx";
import useBureau from "lib/hooks/useBureau.ts";
import CustomComponentSlot from "components/common/CustomComponentSlot";
import { AddOnSection } from "components/add-on/AddOnSection.tsx";
import DelhiveryConfirmationPopUp from "components/checkout/dialogs/DelhiveryConfirmationPopup.tsx";
import { usePopupOrchestrator } from "lib/contexts/PopupOrchestratorProvider.tsx";
import ChoosePlanSection from "components/subscriptions/ChoosePlanSection";
import HideInSubscribe from "components/subscriptions/HideInSubscribe";
interface AuthenticatedViewsProps {
  isPrimaryBtnLoading?: boolean;
}
const ShippingHandlesDialog = React.lazy(() => import("../../checkout/dialogs/ShippingHandlesDialog.tsx"));

const AuthenticatedViews: React.FC<AuthenticatedViewsProps> = ({ isPrimaryBtnLoading = false }) => {
  const { t } = useLocale();
  const [searchParams] = useSearchParams();
  const {
    state: {
      isC2P,
      checkoutView,
      checkoutModal,
      appliedCoupons,
      checkoutValidations,
      uid,
      checkoutId,
      checkoutLoading,
    },
    actions: { setCheckoutModal, setCheckoutView, setCheckoutValidations, setEtdText },
  } = useCheckoutContext();
  const {
    state: { user, isAddressLoading },
  } = useUserContext();
  const {
    state: { merchant, addressConfig, checkoutMetadata },
  } = useMerchantContext();

  const { initialCheckoutStep } = checkoutMetadata ?? {};
  const { openPopup, enqueueAuto, activePopup, closePopup, autoTriggerConfig } = usePopupOrchestrator();
  const { sendAnalyticsEvent } = useSendAnalyticsEvent();
  const { showFullScreenLoader } = useItemEdit();
  const { isAddressNotServicable } = useCheckoutErrors();

  //clarity setup
  useClarity();
  // Need to enable it again after discussion with bureau ends
  // useBureau();

  const selectedAddressId = user?.default_shipping_address?.id ?? "";
  const { hash: hashParam } = useLocation();
  const { isETDEnabled } = useAddressETD(selectedAddressId);
  const [editAddressId, setEditAddressId] = useState<string>("ADDRESS_NEW");
  const [initialLoad, setInitialLoad] = useState(false);
  const hasShownDelhiveryPopup = useRef(false);

  const handleDelhiveryClose = useCallback(() => closePopup(), []);
  const handleDelhiveryChange = useCallback(() => {
    if (Boolean(user?.addresses?.length)) {
      openPopup("ADDRESS_LIST", {});
      return;
    }
    openPopup("ADDRESS_NEW", {});
  }, [user?.addresses?.length, openPopup]);

  const deliveryConfig = merchant?.addressConfig?.deliveryConfig;

  useEffect(() => {
    if (Boolean(hashParam?.length)) {
      handleHashParams();
      return;
    }

    // for setting intial loading state
    const defaultView = searchParams.get("default_view");
    if (defaultView === "PAYMENTS") {
      setCheckoutView("PAYMENTS");
    } else {
      setCheckoutView("ADDRESS_LIST");
    }
  }, []);

  // Handle C2P and initial checkout step logic
  useEffect(() => {
    const isAuthenticated = Boolean(user?.uid);
    const shouldSetPaymentsView = isC2P || initialCheckoutStep === "PAYMENTS";

    if (shouldSetPaymentsView && isAuthenticated) {
      setCheckoutView("PAYMENTS");
      verifyDefaultAddress();

      // Send analytics if user has addresses
      if (user?.addresses?.length) {
        sendAnalyticsEvent({
          eventName: analyticsEvents.FLO_ADDRESS_SELECTED,
          eventType: "click",
        });
      }
    }
  }, [isC2P, initialCheckoutStep, user?.uid, user?.addresses?.length]);

  // Handle address management and modal logic
  useEffect(() => {
    const hasUserAddresses =
      user?.addresses?.find((address) => address?.source === "SHIPPING_ADDRESS") ?? false;
    const isAddressModalOpen = activePopup?.key === "ADDRESS_NEW";
    const openAddressSelection =
      !checkoutLoading && !isAddressLoading && hasUserAddresses && !selectedAddressId;

    // If no address open address form modal
    if (!isAddressLoading && !hasUserAddresses) {
      enqueueAuto("ADDRESS_NEW", {});
      return;
    }
    // Before showing delhivery popup: if the selected address fails the same
    // validation we run on address-form submit, open the edit modal instead.
    const selectedAddress = user?.addresses?.find((address) => address?.id === selectedAddressId);
    const canValidateSelectedAddress =
      !checkoutLoading &&
      !isAddressLoading &&
      hasUserAddresses &&
      selectedAddressId &&
      selectedAddress &&
      !hasShownDelhiveryPopup.current;

    if (canValidateSelectedAddress) {
      const { available, errorFieldData } = validateSelectedAddress({
        selectedAddress,
        addressConfig,
        t,
      });

      if (!available) {
        hasShownDelhiveryPopup.current = true;
        setEditAddressId(selectedAddressId);
        setCheckoutValidations({
          ...checkoutValidations,
          address: {
            isValid: false,
            metadata: {
              addressId: selectedAddressId,
              fieldData: errorFieldData,
            },
          },
        });
        enqueueAuto("ADDRESS_EDIT", { addressId: selectedAddressId });
        return;
      }
    }

    const shouldShowDelhiveryConfirmation =
      !checkoutLoading &&
      !isAddressLoading &&
      hasUserAddresses &&
      selectedAddressId &&
      !hasShownDelhiveryPopup.current &&
      !isAddressNotServicable &&
      !isC2P;

    if (shouldShowDelhiveryConfirmation) {
      hasShownDelhiveryPopup.current = true;
      enqueueAuto("DELHIVERY_CONFIRMATION", {});
      return;
    }
    // On sucess save close modal
    if (isAddressModalOpen && hasUserAddresses) {
      closePopup();
    }

    // If no default address open address list modal
    if (openAddressSelection) {
      const isSinglePageCheckout = initialCheckoutStep === "PAYMENTS";
      isSinglePageCheckout ? enqueueAuto("ADDRESS_LIST", {}) : setCheckoutView("ADDRESS_LIST");
    }
  }, [user?.addresses?.length, isAddressLoading, selectedAddressId, checkoutLoading, initialCheckoutStep]);

  const handleHashParams = () => {
    const hashValue = hashParam.slice(1);
    switch (hashValue) {
      case "PAYMENTS":
      case "COD":
        setCheckoutView("PAYMENTS");
        return;
      default:
        return;
    }
  };

  const verifyDefaultAddress = async () => {
    const selectedAddressId = user?.default_shipping_address?.id ? user?.default_shipping_address?.id : "";
    const selectedAddress = user?.addresses?.find(
      (address: AddressCardFields) => address?.id === selectedAddressId,
    );

    if (Boolean(isETDEnabled) && Boolean(selectedAddressId)) {
      const etdResponse = await getRequest(
        `/checkout/${uid}/address/${selectedAddressId}/etd`,
        "KRATOS_PRIVATE",
      );
      setEtdText({ addressId: selectedAddressId, text: getETDText(etdResponse) });
    }
    if (!selectedAddress) {
      return;
    }
    const addressValidity = validateAddressFields({
      address: selectedAddress,
      config: addressConfig,
      checkoutId,
    });
    if (!addressValidity.available) {
      handleEditAddress(selectedAddressId);
      setCheckoutValidations({
        ...checkoutValidations,
        address: {
          isValid: false,
          metadata: {
            addressId: selectedAddressId,
            fieldData: addressValidity?.errorFieldData,
          },
        },
      });
      return;
    }
    setCheckoutValidations({
      ...checkoutValidations,
      address: {
        isValid: true,
      },
    });
  };

  const handleEditAddress = (id: string) => {
    setEditAddressId(id);
    openPopup("ADDRESS_EDIT", { addressId: id });
  };

  const handleDeliveryAddressBtnClick = () => {
    if (isC2P) return;
    sendAnalyticsEvent({
      eventName: analyticsEvents.FLO_DELIVERY_ADDRESS_CHANGE_CLICKED,
      eventType: "click",
    });
    setCheckoutView("ADDRESS_LIST");
  };

  const addressSkeleton = (
    <div className="space-y-2">
      <div className="skeleton-loader h-[16px] rounded-md w-2/5"></div>
      <div className="skeleton-loader h-[16px] rounded-md w-full"></div>
    </div>
  );

  const deliverySkeleton = (
    <div className="px-3 py-2 space-y-2">
      <div className="skeleton-loader h-[16px] rounded-md w-2/5"></div>
      <div className="skeleton-loader h-[16px] rounded-md w-full"></div>
    </div>
  );

  const billingSkeleton = (
    <div className="px-3 py-2">
      <div className="skeleton-loader h-[16px] rounded-md w-full"></div>
    </div>
  );

  const renderAddressList = () => {
    return (
      <>
        <div id="address-list-section" className="border-base mx-3 mt-3 flex flex-col bg-white">
          <div
            className={`flex w-full flex-col gap-4 overflow-x-hidden rounded-b-[1.75rem] px-3 pt-4 ${
              initialCheckoutStep === "ADDRESS" ? "pb-4" : "pb-3"
            }`}>
            <AnimateLoading loading={checkoutLoading}>
              <AnimateLoading.Skeleton>{addressSkeleton}</AnimateLoading.Skeleton>
              <AnimateLoading.Content>
                <div className="flex w-full items-center justify-between space-x-2">
                  <div className="flex space-x-2">
                    <Truck className="h-4 w-4 text-coal-dark outline-none" strokeWidth={2.5} />
                    <h2 className="text-sm font-medium text-coal-dark">{t("delivering_to_address")}</h2>
                  </div>
                  <div
                    id="flo_add__address"
                    className="flex items-center space-x-2 hover:bg-gray-lighter p-2 rounded-md cursor-pointer"
                    onClick={() => {
                      setEditAddressId("ADDRESS_NEW");
                      openPopup("ADDRESS_NEW", {});
                    }}>
                    <span className="text-sm font-semibold text-coal-dark">Add new</span>
                    <PlusCircle size={20} />
                  </div>
                </div>
                {deliveryConfig?.isEnabled && Boolean(selectedAddressId) && (
                  <div className="flex justify-center items-center bg-primary-alpha05 rounded-xl p-2 mt-2">
                    <span className="min-w-0 text-center text-xs font-medium text-coal-dark">
                      {deliveryConfig?.deliveryText}
                    </span>
                  </div>
                )}
              </AnimateLoading.Content>
            </AnimateLoading>
            <div>
              <div className="flex flex-col gap-4">
                <AddressModify
                  handleEditAddress={handleEditAddress}
                  isPrimaryBtnLoading={isPrimaryBtnLoading}
                />
                <CustomComponentSlot position="SHIPPING_ADDRESS_SECTION" />
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      {checkoutView === "ADDRESS_LIST" && renderAddressList()}

      {checkoutView === "PAYMENTS" && initialCheckoutStep !== "PAYMENTS" && (
        <>
          <div className="flex flex-col space-y-3 mx-3 mt-3">
            <DeliveryCard handleDeliveryAddressBtnClick={handleDeliveryAddressBtnClick} />
            {/* Billing Address */}
            <AnimateLoading loading={checkoutLoading}>
              <AnimateLoading.Skeleton>{billingSkeleton}</AnimateLoading.Skeleton>
              <AnimateLoading.Content
                motionProps={{
                  className: "!mt-0",
                }}>
                <BillingAddress />
                <CustomComponentSlot position="BILLING_ADDRESS_SECTION" />
              </AnimateLoading.Content>
            </AnimateLoading>
            {merchant?.showShippingOnPayments && (
              <div className="w-full">
                <ShippingHandlesSection loadPayments={() => setInitialLoad(true)} />
                <CustomComponentSlot position="SHIPPING_HANDLES_SECTION" />
              </div>
            )}
            <HideInSubscribe>
              <Rewards />
              <CustomComponentSlot position="REWARDS_SECTION" />
              <AnimateExit
                isChildVisible={Boolean(checkoutMetadata?.uiAttributes?.upsell?.isEnabled) && !isC2P}
                loading={checkoutLoading}>
                <AnimateExit.Skeleton>
                  <UpsellSkeleton />
                </AnimateExit.Skeleton>
                <AnimateExit.Content
                  motionProps={{
                    className: "!mt-0",
                  }}>
                  <UpSellAndWishlistSection />
                  <CustomComponentSlot position="UPSELL_SECTION" />
                </AnimateExit.Content>
              </AnimateExit>
              <AnimateLoading loading={checkoutLoading} size="sm">
                <AnimateLoading.Content
                  motionProps={{
                    className: "!mt-0",
                  }}>
                  <AddOnSection />
                  <CustomComponentSlot position="ADD_ON_SECTION" />
                </AnimateLoading.Content>
              </AnimateLoading>
            </HideInSubscribe>
            <ChoosePlanSection />
            <CustomComponentSlot position="SUBSCRIPTION_PLAN_SECTION" />
            <Suspense fallback={<PaymentSkeleton showGyftrLoading={true} />}>
              <Payments />
            </Suspense>
            <CustomComponentSlot position="PAYMENTS_SECTION" />
          </div>
        </>
      )}
      {checkoutView === "PAYMENTS" && initialCheckoutStep === "PAYMENTS" && (
        <>
          <div className="flex flex-col space-y-3 mx-3 mt-3">
            <AnimateLoading loading={checkoutLoading}>
              <AnimateLoading.Skeleton>{deliverySkeleton}</AnimateLoading.Skeleton>
              <AnimateLoading.Content>
                <AddressSection />
                <CustomComponentSlot position="SHIPPING_ADDRESS_SECTION" />
              </AnimateLoading.Content>
            </AnimateLoading>
            <AnimateLoading loading={checkoutLoading}>
              <AnimateLoading.Skeleton>{billingSkeleton}</AnimateLoading.Skeleton>
              <AnimateLoading.Content
                motionProps={{
                  className: "!mt-0",
                }}>
                <BillingAddress />
                <CustomComponentSlot position="BILLING_ADDRESS_SECTION" />
              </AnimateLoading.Content>
            </AnimateLoading>
            <AnimateLoading loading={checkoutLoading}>
              <AnimateLoading.Skeleton>{deliverySkeleton}</AnimateLoading.Skeleton>
              <AnimateLoading.Content>
                <ShippingHandlesSection loadPayments={() => setInitialLoad(true)} />
                <CustomComponentSlot position="SHIPPING_HANDLES_SECTION" />
              </AnimateLoading.Content>
            </AnimateLoading>
            <HideInSubscribe>
              <AnimateExit
                isChildVisible={Boolean(hasAutoAppliedCoupon(appliedCoupons) || merchant?.isDiscountEnabled)}
                loading={checkoutLoading}>
                <AnimateExit.Skeleton>{deliverySkeleton}</AnimateExit.Skeleton>
                <AnimateExit.Content
                  motionProps={{
                    className: "!mt-0",
                  }}>
                  <Coupons />
                  <CustomComponentSlot position="COUPONS_SECTION" />
                </AnimateExit.Content>
              </AnimateExit>
            </HideInSubscribe>
            <HideInSubscribe>
              {!initialLoad ? deliverySkeleton : (
                <>
                  <Rewards />
                  <CustomComponentSlot position="REWARDS_SECTION" />
                </>
              )}
              <AnimateExit
                isChildVisible={
                  initialCheckoutStep === "PAYMENTS" &&
                  Boolean(checkoutMetadata?.uiAttributes?.upsell?.isEnabled) &&
                  !isC2P
                }
                loading={!initialLoad}>
                <AnimateExit.Skeleton>
                  <UpsellSkeleton />
                </AnimateExit.Skeleton>
                <AnimateExit.Content
                  motionProps={{
                    className: "!mt-0",
                  }}>
                  <UpSellAndWishlistSection />
                  <CustomComponentSlot position="UPSELL_SECTION" />
                </AnimateExit.Content>
              </AnimateExit>
              <AnimateLoading loading={checkoutLoading} size="sm">
                <AnimateLoading.Content
                  motionProps={{
                    className: "!mt-0",
                  }}>
                  <AddOnSection />
                  <CustomComponentSlot position="ADD_ON_SECTION" />
                </AnimateLoading.Content>
              </AnimateLoading>
            </HideInSubscribe>
            <ChoosePlanSection />
            <CustomComponentSlot position="SUBSCRIPTION_PLAN_SECTION" />
            {!initialLoad ? <PaymentSkeleton showGyftrLoading={true} /> : (
              <>
                <Payments />
                <CustomComponentSlot position="PAYMENTS_SECTION" />
              </>
            )}
          </div>
        </>
      )}

      <GenericDialog
        isOpen={activePopup?.key === "ADDRESS_LIST"}
        setIsOpen={() => {
          closePopup();
        }}
        translateAxis="y"
        dialogOverlay={true}
        customClass="h-[92%] md:h-[77vh] md:top-[8vh]"
        modalType="ADDRESS_LIST">
        <AddressListDialog setIsOpen={() => closePopup()} isPrimaryBtnLoading={isPrimaryBtnLoading} />
      </GenericDialog>

      <GenericDialog
        isOpen={activePopup?.key === "ADDRESS_NEW" || activePopup?.key === "ADDRESS_EDIT"}
        setIsOpen={() => {
          closePopup();
          setInitialLoad(true);
        }}
        translateAxis="y"
        dialogOverlay={true}
        customClass="h-[92%] md:h-[77vh] md:top-[8vh]"
        modalType="ADDRESS_NEW">
        <AddressForm
          addressId={editAddressId}
          initialFormErrors={checkoutValidations.address?.metadata?.fieldData}
        />
      </GenericDialog>

      <GenericDialog
        isOpen={activePopup?.key === "SHIPPING_HANDLES"}
        setIsOpen={() => closePopup()}
        translateAxis="y"
        customClass="overflow-scroll md:!top-auto md:absolute rounded-t-2xl max-h-[81vh]"
        dialogOverlay={true}
        modalType="SHIPPING_HANDLES">
        <ShippingHandlesDialog />
      </GenericDialog>

      <GenericDialog
        isOpen={activePopup?.key === "OOS"}
        setIsOpen={() => closePopup()}
        translateAxis="y"
        dialogOverlay={true}
        customClass="overflow-scroll md:!top-auto md:absolute rounded-t-2xl max-h-[81vh]"
        modalType="OOS"
        disableExitAnimation={activePopup !== null}>
        <OOSDialog />
      </GenericDialog>

      <DelhiveryConfirmationPopUp
        isOpen={activePopup?.key === "DELHIVERY_CONFIRMATION"}
        closePopup={handleDelhiveryClose}
        onChangeClick={handleDelhiveryChange}
        address={user?.default_shipping_address}
        durationSeconds={autoTriggerConfig.DELHIVERY_CONFIRMATION.duration ?? 3000}
      />
      {showFullScreenLoader && <OverlaySpinner />}
    </>
  );
};

export default AuthenticatedViews;
